import React from 'react'
import { PtsCanvas } from 'react-pts-canvas'
import { CanvasSpace, Bound, Group, CanvasForm } from 'pts'

import CoreController from './controllers/CoreController'
import { FunnelOptions } from './types'

type FunnelPTSProps = { options: FunnelOptions } & React.ComponentProps<
  typeof PtsCanvas
>

const FunnelPTS: React.FC<FunnelPTSProps> = ({ options, ...canvasProps }) => {
  const ctrlRef = React.useRef(new CoreController())
  const spaceRef = React.useRef<CanvasSpace>(null)
  const formRef = React.useRef<CanvasForm>(null)

  const handleReady = React.useCallback(
    (space: CanvasSpace, form: CanvasForm, _bound: Bound) => {
      spaceRef.current = space
      formRef.current = form
      ctrlRef.current.init(space, form, options)
    },
    [options]
  )

  const handleAnimate = React.useCallback(
    (_space: CanvasSpace, _form: CanvasForm, time: number, ftime: number) => {
      ctrlRef.current.render(time, ftime)
    },
    []
  )

  const handleAction = React.useCallback(
    (
      _space: CanvasSpace,
      _form: CanvasForm,
      type: string,
      px: number,
      py: number,
      evt: Event
    ) => {
      ctrlRef.current.handleActions(type, px, py, evt)
    },
    []
  )

  const handleResize = React.useCallback(
    (_space: CanvasSpace, _form: CanvasForm, size: Group, evt: Event) => {
      ctrlRef.current.handleResize(size, evt)
    },
    []
  )

  React.useEffect(() => {
    if (spaceRef.current && formRef.current) {
      ctrlRef.current.init(spaceRef.current, formRef.current, options)
    }
  }, [options])

  return (
    <PtsCanvas
      onReady={handleReady}
      onAnimate={handleAnimate}
      onAction={handleAction}
      onPtsResize={handleResize}
      {...canvasProps}
    />
  )
}

interface FunnelProps {
  options: FunnelOptions
}

const Funnel: React.FC<FunnelProps> = React.memo(({ options }) => {
  return (
    <FunnelPTS
      options={options}
      style={{ width: '100%', height: '100%' }}
      background="white"
    />
  )
})

Funnel.displayName = 'Funnel'

export default Funnel
